import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Car, ImagePlus, Loader2, Search, ImageOff, Star, Pencil, Trash2, X, Check, Plus, Copy, Sparkles, Images } from "lucide-react";
import { toast } from "sonner";
import { TestiSeoModal } from "@/components/TestiSeoModal";
import { FotoGestione } from "@/components/FotoGestione";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-xmove-ink/15 bg-xmove-cream px-3 py-2 text-sm text-xmove-ink outline-none focus:border-xmove-orange";

const vuota = () => ({ durata_mesi: "", km_annui: "", canone: "", pronta_consegna: true });

const normalizza = (righe) =>
  righe.map((r) => ({ ...r, canone: r.canone ?? "", durata_mesi: r.durata_mesi ?? "", km_annui: r.km_annui ?? "" }));

const analizza = (righe) => {
  const valide = righe.filter((r) => r.canone !== "" && r.durata_mesi !== "" && r.km_annui !== "");
  const minimo = valide.length ? Math.min(...valide.map((r) => Number(r.canone))) : null;
  const chiavi = valide.map((r) => `${r.durata_mesi}-${r.km_annui}`);
  const duplicati = new Set(chiavi.filter((k, i) => chiavi.indexOf(k) !== i));
  const payload = valide.map((r) => ({ ...r, canone: Number(r.canone), durata_mesi: Number(r.durata_mesi), km_annui: Number(r.km_annui) }));
  return { valide, minimo, duplicati, payload };
};

const cellCls = "w-full rounded-md border border-xmove-ink/15 bg-xmove-cream px-2 py-1.5 text-sm text-xmove-ink outline-none focus:border-xmove-orange";

const VariantiEditor = ({ righe, setRighe, prefix = "edit" }) => {
  const { minimo, duplicati } = analizza(righe);
  const setCella = (i, k, val) => setRighe(righe.map((r, j) => (j === i ? { ...r, [k]: val } : r)));
  const rimuovi = (i) => setRighe(righe.filter((_, j) => j !== i));
  const aggiungi = () => setRighe([...righe, vuota()]);
  const duplica = (i) => setRighe([...righe.slice(0, i + 1), { ...righe[i], id_offerta: null, canone: "" }, ...righe.slice(i + 1)]);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left" data-testid={`${prefix}-varianti-table`}>
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-wider text-xmove-muted">
              <th className="pb-1.5 pr-2">Durata mesi</th>
              <th className="pb-1.5 pr-2">Km/anno</th>
              <th className="pb-1.5 pr-2">Canone €/mese</th>
              <th className="pb-1.5 pr-2">Pronta</th>
              <th className="pb-1.5" />
            </tr>
          </thead>
          <tbody>
            {righe.map((r, i) => {
              const dup = r.durata_mesi !== "" && r.km_annui !== "" && duplicati.has(`${r.durata_mesi}-${r.km_annui}`);
              const isMin = minimo != null && r.canone !== "" && Number(r.canone) === minimo;
              return (
                <tr key={i} className={dup ? "bg-red-50" : ""} data-testid={`${prefix}-variante-row-${i}`}>
                  <td className="py-1 pr-2"><input type="number" min="1" value={r.durata_mesi} onChange={(e) => setCella(i, "durata_mesi", e.target.value)} className={cellCls} data-testid={`${prefix}-durata-${i}`} placeholder="36" /></td>
                  <td className="py-1 pr-2"><input type="number" min="1" step="1000" value={r.km_annui} onChange={(e) => setCella(i, "km_annui", e.target.value)} className={cellCls} data-testid={`${prefix}-km-${i}`} placeholder="10000" /></td>
                  <td className="py-1 pr-2">
                    <div className="relative">
                      <input type="number" min="1" value={r.canone} onChange={(e) => setCella(i, "canone", e.target.value)} className={`${cellCls} ${isMin ? "border-xmove-orange font-bold" : ""}`} data-testid={`${prefix}-canone-${i}`} placeholder="399" />
                      {isMin && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold uppercase text-xmove-orange">base</span>}
                    </div>
                  </td>
                  <td className="py-1 pr-2 text-center">
                    <input type="checkbox" checked={!!r.pronta_consegna} onChange={(e) => setCella(i, "pronta_consegna", e.target.checked)} className="h-4 w-4 accent-xmove-orange" data-testid={`${prefix}-pronta-${i}`} />
                  </td>
                  <td className="py-1">
                    <span className="flex items-center gap-1">
                      <button type="button" onClick={() => duplica(i)} title="Duplica riga" data-testid={`${prefix}-dup-${i}`} className="rounded-md border border-xmove-ink/15 p-1.5 text-xmove-muted transition-colors hover:border-xmove-night hover:text-xmove-night"><Copy size={12} /></button>
                      <button type="button" onClick={() => rimuovi(i)} title="Rimuovi riga" data-testid={`${prefix}-remove-${i}`} className="rounded-md border border-red-200 p-1.5 text-red-400 transition-colors hover:bg-red-500 hover:text-white"><Trash2 size={12} /></button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {duplicati.size > 0 && <p className="mt-1 text-[11px] font-semibold text-red-500" data-testid={`${prefix}-dup-warning`}>Combinazioni durata/km duplicate evidenziate in rosso.</p>}
      <button type="button" onClick={aggiungi} data-testid={`${prefix}-add-row`} className="mt-3 flex items-center gap-1.5 rounded-lg border border-dashed border-xmove-ink/25 px-3 py-2 text-xs font-bold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange">
        <Plus size={13} /> Aggiungi combinazione
      </button>
    </div>
  );
};

const EditForm = ({ v, onClose, onSaved }) => {
  const [righe, setRighe] = useState(() =>
    normalizza(v.varianti?.length ? v.varianti : [{ canone: v.canone, durata_mesi: v.durata_mesi, km_annui: v.km_annui, pronta_consegna: true }])
  );
  const [consegna, setConsegna] = useState(v.tempi_consegna ?? "");
  const [anag, setAnag] = useState({ marca: v.marca ?? "", modello: v.modello ?? "", allestimento: v.allestimento ?? "", alimentazione: v.alimentazione ?? "", cambio: v.cambio ?? "" });
  const [saving, setSaving] = useState(false);
  const { valide, minimo, duplicati, payload } = analizza(righe);
  const setA = (k) => (e) => setAnag({ ...anag, [k]: e.target.value });
  const anagCambiata = ["marca", "modello", "allestimento", "alimentazione", "cambio"].some((k) => (anag[k] || "") !== (v[k] || ""));

  const salva = async () => {
    if (!anag.marca.trim() || !anag.modello.trim()) {
      toast.error("Marca e modello sono obbligatori.");
      return;
    }
    if (duplicati.size) {
      toast.error("Ci sono combinazioni durata/km duplicate: correggile prima di salvare.");
      return;
    }
    setSaving(true);
    try {
      let url = null;
      if (anagCambiata) {
        const r = await axios.post(`${API}/admin/veicoli/${v.id_motorizzazione}/anagrafica`, {
          marca: anag.marca, modello: anag.modello, allestimento: anag.allestimento,
          alimentazione: anag.alimentazione || null, cambio: anag.cambio || null,
        });
        url = r.data.url;
      }
      await axios.post(`${API}/admin/veicoli/${v.id_motorizzazione}/varianti`, { tempi_consegna: consegna, varianti: payload });
      const prezzo = valide.length ? `Canone base: €${minimo}/mese.` : "Canone su richiesta.";
      toast.success(url ? `Veicolo rinominato (nuovo indirizzo ${url}). ${prezzo}` : `Salvate ${valide.length} combinazioni. ${prezzo}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito, riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-xmove-ink/8 pt-3" data-testid={`veicolo-edit-${v.id_motorizzazione}`}>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Anagrafica veicolo</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Marca *</span>
          <input type="text" value={anag.marca} onChange={setA("marca")} className={inputCls} data-testid="edit-marca" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Modello *</span>
          <input type="text" value={anag.modello} onChange={setA("modello")} className={inputCls} data-testid="edit-modello" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Allestimento</span>
          <input type="text" value={anag.allestimento} onChange={setA("allestimento")} className={inputCls} data-testid="edit-allestimento" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Alimentazione</span>
          <select value={anag.alimentazione} onChange={setA("alimentazione")} className={inputCls} data-testid="edit-alimentazione">
            <option value="">—</option>
            {["Benzina", "Diesel", "Hybrid", "Plug-in Hybrid", "Elettrico"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Cambio</span>
          <select value={anag.cambio} onChange={setA("cambio")} className={inputCls} data-testid="edit-cambio">
            <option value="">—</option>
            {["Manuale", "Automatico", "Semi-automatico"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Consegna</span>
          <input type="text" value={consegna} onChange={(e) => setConsegna(e.target.value)} className={inputCls} data-testid="edit-consegna" placeholder="Es. Pronta consegna" />
        </label>
      </div>
      {anagCambiata && (
        <p className="mt-1.5 text-[11px] text-xmove-orange" data-testid="edit-anag-warning">
          Rinominando il veicolo cambia anche l'indirizzo della pagina e il nome viene aggiornato nei testi SEO.
        </p>
      )}

      <p className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Combinazioni di prezzo (durata / km / canone) — la più economica è il canone base</p>
      <VariantiEditor righe={righe} setRighe={setRighe} prefix="edit" />
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button onClick={salva} disabled={saving} data-testid="edit-save" className="flex items-center gap-1.5 rounded-lg bg-xmove-orange px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-xmove-orangeDark disabled:opacity-60">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salva modifiche
        </button>
        <button onClick={onClose} data-testid="edit-cancel" className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 px-4 py-2 text-xs font-bold text-xmove-muted transition-colors hover:text-xmove-ink">
          <X size={13} /> Annulla
        </button>
      </div>
    </div>
  );
};

const NuovoForm = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ marca: "", modello: "", allestimento: "", alimentazione: "", cambio: "", tempi_consegna: "" });
  const [righe, setRighe] = useState([vuota()]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const { valide, minimo, duplicati, payload } = analizza(righe);

  const crea = async () => {
    if (!form.marca.trim() || !form.modello.trim()) {
      toast.error("Marca e modello sono obbligatori.");
      return;
    }
    if (duplicati.size) {
      toast.error("Ci sono combinazioni durata/km duplicate: correggile prima di pubblicare.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/veicoli/nuovo`, {
        marca: form.marca, modello: form.modello, allestimento: form.allestimento,
        alimentazione: form.alimentazione || null, cambio: form.cambio || null,
        tempi_consegna: form.tempi_consegna || null, varianti: payload,
      });
      toast.success(valide.length ? `Offerta online con ${valide.length} combinazioni (da €${minimo}/mese): ${r.data.url}` : `Offerta online a canone su richiesta: ${r.data.url}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Creazione fallita, riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border-2 border-xmove-orange/30 bg-white p-4" data-testid="veicolo-new-form">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Marca *</span>
          <input type="text" value={form.marca} onChange={set("marca")} className={inputCls} data-testid="new-marca" placeholder="Es. Fiat" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Modello *</span>
          <input type="text" value={form.modello} onChange={set("modello")} className={inputCls} data-testid="new-modello" placeholder="Es. Panda" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Allestimento</span>
          <input type="text" value={form.allestimento} onChange={set("allestimento")} className={inputCls} data-testid="new-allestimento" placeholder="Es. 1.0 Hybrid" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Alimentazione</span>
          <select value={form.alimentazione} onChange={set("alimentazione")} className={inputCls} data-testid="new-alimentazione">
            <option value="">—</option>
            {["Benzina", "Diesel", "Hybrid", "Plug-in Hybrid", "Elettrico"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Cambio</span>
          <select value={form.cambio} onChange={set("cambio")} className={inputCls} data-testid="new-cambio">
            <option value="">—</option>
            {["Manuale", "Automatico", "Semi-automatico"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Consegna</span>
          <input type="text" value={form.tempi_consegna} onChange={set("tempi_consegna")} className={inputCls} data-testid="new-consegna" placeholder="Es. Pronta consegna" />
        </label>
      </div>

      <p className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Combinazioni di prezzo (durata / km / canone)</p>
      <VariantiEditor righe={righe} setRighe={setRighe} prefix="new" />
      <p className="mt-2 text-[11px] text-xmove-muted">
        Inserisci tutte le combinazioni: la più economica diventa il canone "da" della card e la pagina avrà subito il calcolatore anticipo attivo. Senza combinazioni l'offerta appare come "Canone su richiesta".
      </p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button onClick={crea} disabled={saving} data-testid="new-save" className="flex items-center gap-1.5 rounded-lg bg-xmove-orange px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-xmove-orangeDark disabled:opacity-60">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Pubblica offerta{valide.length ? ` (${valide.length} combinazioni)` : ""}
        </button>
        <button onClick={onClose} data-testid="new-cancel" className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 px-4 py-2 text-xs font-bold text-xmove-muted transition-colors hover:text-xmove-ink">
          <X size={13} /> Annulla
        </button>
      </div>
    </div>
  );
};

export const VeicoliGestione = () => {
  const [veicoli, setVeicoli] = useState([]);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [testiPer, setTestiPer] = useState(null);
  const [fotoPer, setFotoPer] = useState(null);
  const [eliminati, setEliminati] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const fileRef = useRef(null);
  const [target, setTarget] = useState(null);

  const load = () => axios.get(`${API}/admin/veicoli`).then((r) => setVeicoli(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pick = (mid) => {
    setTarget(mid);
    fileRef.current?.click();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !target) return;
    setUploading(target);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/veicoli/${target}/foto`, fd);
      toast.success("Foto caricata e collegata al veicolo.");
      load();
    } catch {
      toast.error("Caricamento fallito: usa un'immagine JPG o PNG sotto 10MB.");
    } finally {
      setUploading(null);
    }
  };

  const toggleVetrina = async (v) => {
    try {
      await axios.post(`${API}/admin/veicoli/${v.id_motorizzazione}/vetrina`, { vetrina: !v.vetrina });
      toast.success(v.vetrina ? "Rimossa dalla vetrina." : "In vetrina: sarà tra le prime offerte del sito.");
      load();
    } catch {
      toast.error("Errore nell'aggiornamento della vetrina.");
    }
  };

  const elimina = async (v) => {
    if (!window.confirm(`Eliminare ${v.marca} ${v.modello} ${v.allestimento} dal sito?\nL'offerta sparirà subito dal catalogo e dall'assistente. La trovi in fondo, in "Offerte eliminate", se cambi idea.`)) return;
    try {
      await axios.post(`${API}/admin/veicoli/${v.id_motorizzazione}/elimina`);
      toast.success("Offerta eliminata dal sito (ripristinabile in fondo alla pagina).");
      load();
      setEliminati(null);
    } catch {
      toast.error("Eliminazione fallita, riprova.");
    }
  };

  const mostraEliminati = async () => {
    if (eliminati !== null) {
      setEliminati(null);
      return;
    }
    try {
      const r = await axios.get(`${API}/admin/veicoli/eliminati/lista`);
      setEliminati(r.data);
    } catch {
      toast.error("Errore nel caricamento delle eliminate.");
    }
  };

  const ripristina = async (v) => {
    try {
      await axios.post(`${API}/admin/veicoli/${v.id_motorizzazione}/ripristina`);
      toast.success(`${v.marca} ${v.modello} di nuovo online.`);
      setEliminati(null);
      load();
    } catch {
      toast.error("Ripristino fallito, riprova.");
    }
  };

  const q = query.trim().toLowerCase();
  const list = q
    ? veicoli.filter((v) => `${v.marca} ${v.modello} ${v.allestimento}`.toLowerCase().includes(q))
    : veicoli;
  const senzaFoto = veicoli.filter((v) => !v.n_foto).length;
  const inVetrina = veicoli.filter((v) => v.vetrina).length;

  return (
    <section className="mt-12" data-testid="veicoli-section">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-xmove-orange">
        <Car size={14} /> Catalogo veicoli
      </p>
      <h2 className="mt-2 font-display text-2xl font-bold text-xmove-ink">Gestione offerte e veicoli</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-xmove-muted">
        Tutto il catalogo in un colpo d'occhio: aggiungi le foto mancanti, scegli le offerte <b>in vetrina</b>
        (appaiono per prime sul sito nell'ordine in cui le attivi), <b>modifica</b> prezzi e condizioni oppure
        <b> elimina</b> un'offerta — il sito si aggiorna subito, assistente incluso.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="veicoli-stats">
        <span className="rounded-full bg-white px-4 py-2 text-xs font-bold text-xmove-ink shadow-soft">
          {veicoli.length} veicoli
        </span>
        <span className="rounded-full bg-white px-4 py-2 text-xs font-bold text-xmove-green shadow-soft">
          {veicoli.length - senzaFoto} con foto
        </span>
        <span className="rounded-full bg-white px-4 py-2 text-xs font-bold text-xmove-orange shadow-soft">
          {senzaFoto} senza foto
        </span>
        <span className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold text-xmove-ink shadow-soft">
          <Star size={12} className="fill-xmove-orange text-xmove-orange" /> {inVetrina} in vetrina
        </span>
        <button
          onClick={() => setShowNew(!showNew)}
          data-testid="veicoli-new-btn"
          className="ml-auto flex items-center gap-1.5 rounded-full bg-xmove-orange px-4 py-2 text-xs font-bold text-white shadow-soft transition-colors hover:bg-xmove-orangeDark"
        >
          <Plus size={14} /> Nuova offerta
        </button>
      </div>

      {showNew && (
        <NuovoForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      )}

      <div className="relative mt-4">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-xmove-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca marca, modello o allestimento…"
          data-testid="veicoli-search-input"
          className="w-full rounded-xl border border-xmove-ink/15 bg-white py-3 pl-10 pr-4 text-sm outline-none focus:border-xmove-orange"
        />
      </div>

      <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="veicoli-file-input" />

      <ul className="mt-4 space-y-2" data-testid="veicoli-list">
        {list.map((v) => (
          <li
            key={v.id_motorizzazione}
            className="rounded-xl border border-xmove-ink/8 bg-white px-4 py-3"
            data-testid={`veicolo-row-${v.id_motorizzazione}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm">
                <span className="font-semibold text-xmove-ink">{v.marca} {v.modello}</span>{" "}
                <span className="text-xmove-muted">{v.allestimento}</span>
                {v.canone != null ? (
                  <span className="ml-2 text-xs font-bold text-xmove-orange">da €{v.canone}/mese</span>
                ) : (
                  <span className="ml-2 text-xs font-bold text-xmove-muted">canone su richiesta</span>
                )}
                {v.vetrina && (
                  <span className="ml-2 rounded-full bg-xmove-orange/12 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-xmove-orange">
                    Vetrina{v.vetrina_ordine ? ` #${v.vetrina_ordine}` : ""}
                  </span>
                )}
                {v.stato === "bozza" && (
                  <span className="ml-2 rounded-full bg-xmove-ink/8 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-xmove-muted">
                    Bozza
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2">
                {v.n_foto ? (
                  <button
                    type="button"
                    onClick={() => setFotoPer(v)}
                    data-testid={`veicolo-foto-manage-${v.id_motorizzazione}`}
                    title="Gestisci foto: riordina, sostituisci, elimina, aggiungi"
                    className="flex items-center gap-1 rounded-lg border border-transparent px-2 py-1 text-xs font-semibold text-xmove-green transition-colors hover:border-xmove-green/40 hover:bg-xmove-green/5"
                  >
                    <Images size={14} /> {v.n_foto} foto · gestisci
                  </button>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-semibold text-xmove-orange">
                    <ImageOff size={14} /> senza foto
                  </span>
                )}
                <button
                  onClick={() => pick(v.id_motorizzazione)}
                  disabled={uploading === v.id_motorizzazione}
                  data-testid={`veicolo-upload-${v.id_motorizzazione}`}
                  title="Carica foto"
                  className="flex items-center justify-center rounded-lg border border-xmove-orange/40 p-2 text-xmove-orange transition-colors hover:bg-xmove-orange hover:text-white disabled:opacity-60"
                >
                  {uploading === v.id_motorizzazione ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={14} />}
                </button>
                <button
                  onClick={() => toggleVetrina(v)}
                  data-testid={`veicolo-vetrina-${v.id_motorizzazione}`}
                  aria-label={v.vetrina ? "Togli dalla vetrina" : "Metti in vetrina"}
                  title={v.vetrina ? "Togli dalla vetrina" : "Metti in vetrina"}
                  className={`flex items-center justify-center rounded-lg border p-2 transition-colors ${
                    v.vetrina
                      ? "border-xmove-orange bg-xmove-orange text-white"
                      : "border-xmove-ink/15 text-xmove-muted hover:border-xmove-orange hover:text-xmove-orange"
                  }`}
                >
                  <Star size={14} className={v.vetrina ? "fill-white" : ""} />
                </button>
                <button
                  onClick={() => setEditing(editing === v.id_motorizzazione ? null : v.id_motorizzazione)}
                  data-testid={`veicolo-edit-${v.id_motorizzazione}`}
                  aria-label="Modifica offerta"
                  title="Modifica offerta"
                  className={`flex items-center justify-center rounded-lg border p-2 transition-colors ${
                    editing === v.id_motorizzazione
                      ? "border-xmove-night bg-xmove-night text-white"
                      : "border-xmove-ink/15 text-xmove-muted hover:border-xmove-night hover:text-xmove-night"
                  }`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => setTestiPer(v)}
                  data-testid={`veicolo-rigenera-${v.id_motorizzazione}`}
                  aria-label="Testo SEO per l'auto (AI)"
                  title="Testo SEO per l'auto (AI): anteprima, correzione e pubblicazione"
                  className="flex items-center justify-center rounded-lg border border-xmove-ink/15 p-2 text-xmove-muted transition-colors hover:border-xmove-orange hover:text-xmove-orange"
                >
                  <Sparkles size={14} />
                </button>
                <button
                  onClick={() => elimina(v)}
                  data-testid={`veicolo-delete-${v.id_motorizzazione}`}
                  aria-label="Elimina offerta"
                  title="Elimina offerta"
                  className="flex items-center justify-center rounded-lg border border-red-200 p-2 text-red-400 transition-colors hover:border-red-500 hover:bg-red-500 hover:text-white"
                >
                  <Trash2 size={14} />
                </button>
              </span>
            </div>
            {editing === v.id_motorizzazione && (
              <EditForm v={v} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
            )}
          </li>
        ))}
        {!list.length && (
          <li className="rounded-xl border border-dashed border-xmove-ink/20 bg-white px-4 py-6 text-center text-sm text-xmove-muted">
            Nessun veicolo trovato per questa ricerca.
          </li>
        )}
      </ul>

      <div className="mt-6 border-t border-xmove-ink/8 pt-4">
        <button
          onClick={mostraEliminati}
          data-testid="veicoli-eliminati-toggle"
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-xmove-muted transition-colors hover:text-xmove-ink"
        >
          <Trash2 size={13} /> Offerte eliminate {eliminati ? `(${eliminati.length})` : ""}
        </button>
        {eliminati !== null && (
          <ul className="mt-3 space-y-2" data-testid="veicoli-eliminati-list">
            {eliminati.filter((v) => !v.marca?.startsWith("ZZ Test")).map((v) => (
              <li key={v.id_motorizzazione} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-red-200 bg-red-50/40 px-4 py-3">
                <span className="text-sm text-xmove-muted">
                  <span className="font-semibold">{v.marca} {v.modello}</span> {v.allestimento}
                  {v.canone != null && <span className="ml-2 text-xs">da €{v.canone}/mese</span>}
                </span>
                <button
                  onClick={() => ripristina(v)}
                  data-testid={`veicolo-ripristina-${v.id_motorizzazione}`}
                  className="flex items-center gap-1.5 rounded-lg border border-xmove-green/50 px-3 py-1.5 text-xs font-bold text-xmove-green transition-colors hover:bg-xmove-green hover:text-white"
                >
                  <Check size={13} /> Ripristina
                </button>
              </li>
            ))}
            {!eliminati.length && (
              <li className="text-xs text-xmove-muted">Nessuna offerta eliminata.</li>
            )}
          </ul>
        )}
      </div>
      {fotoPer && <FotoGestione veicolo={fotoPer} onClose={() => setFotoPer(null)} onChanged={load} />}
      {testiPer && (
        <TestiSeoModal
          veicolo={testiPer}
          onClose={() => setTestiPer(null)}
          onPublished={() => {
            setTestiPer(null);
            load();
          }}
        />
      )}
    </section>
  );
};
